import React from "react";

import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { apiClient } from "../api/client";
import { ProgramDateExtended } from "../api/PartnerPortalGatewayApi";
import { MainLayout } from "../components/layout/MainLayout";
import { CACard, UniversalDataGrid } from "../components/ui";
import { useAppSelector } from "../store/hooks";

const RocketeersPage: React.FC = () => {
  const selectedService = useAppSelector((state) => state.service.selectedService);
  const selectedServiceId = selectedService?.serviceId;

  // Fetch upcoming program activities
  const {
    data: upcomingActivities,
    isLoading: isLoadingActivities,
    error: activitiesError,
  } = useQuery({
    queryKey: ["rocketeersUpcomingActivities", selectedServiceId],
    queryFn: () => apiClient.holidayClub.getUpcomingProgramDateActivities(selectedServiceId!),
    enabled: !!selectedServiceId,
  });

  // Handle errors
  // React.useEffect(() => {
  //   if (activitiesError) {
  //     showError("Failed to load upcoming Rocketeers activities");
  //   }
  // }, [activitiesError, showError]);

  // Upcoming Activities Grid
  const upcomingActivitiesColumns: GridColDef[] = [
    {
      field: "date",
      headerName: "Program Date",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => dayjs(params.row.date).format("DD-MMM-YYYY"),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 200,
    },
    {
      field: "feeDescription",
      headerName: "Fee Type",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "statusName",
      headerName: "Status",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (params.row.isClosed ? "Closed" : "Open"),
    },
    {
      field: "activityTypeName",
      headerName: "Activity",
      flex: 2,
      minWidth: 200,
      renderCell: (params) => `${params.row.activityHeading} (${params.row.activityTypeName})`,
    },
  ];

  const upcomingActivitiesRows: GridRowsProp =
    upcomingActivities?.map((activity: ProgramDateExtended, index: number) => ({
      id: `${activity.programDateId}_${index}`,
      rowNumber: index + 1,
      ...activity,
    })) || [];

  if (isLoadingActivities) {
    return (
      <MainLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (activitiesError) {
    return (
      <MainLayout>
        <Alert severity="error">Error loading Rocketeers data: {(activitiesError as Error)?.message}</Alert>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Upcoming Program Section */}
        <CACard>
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" component="h1" gutterBottom>
              Rocketeers Upcoming Program
            </Typography>
            <UniversalDataGrid
              rows={upcomingActivitiesRows}
              columns={upcomingActivitiesColumns}
              loading={isLoadingActivities}
              pageSize={10}
            />
          </Box>
        </CACard>
      </Box>
    </MainLayout>
  );
};

export default RocketeersPage;
